import React from "react";

/**
 * N8Emblem — the dimensional N8ONE orb. One image asset, three modes.
 *
 * All three modes derive from the SAME emblem file in /public/brand/.
 * The emblem is NEVER re-interpreted or re-drawn here; "simplified"
 * only tightens the visual bed around it (removes glow furniture) so
 * the mark reads cleanly at small sizes, and "watermark" simply drops
 * opacity. The N8, sun, path, and orb silhouette stay exactly as the
 * brand file ships them.
 *
 * MODES
 *   full        — the dimensional orb at meaningful moments
 *                 (onboarding, sign-in, Soul Compass intro, Soul Work
 *                 intros, Becoming hero, completion moments, and the
 *                 N8 Guide card).
 *   simplified  — the compact mark for small inline contexts (nav
 *                 pulses, chips, toasts, inline body copy).
 *   watermark   — ultra-low opacity (3–6%) decorative background.
 *                 Non-interactive, aria-hidden.
 *
 * GLOBAL IDENTITY RULE (2026-02 differentiation)
 *   The physical N8 orb is NEVER moved, scaled, rotated, bounced, or
 *   pulsed — anywhere.
 *
 *   `breathing` is the single switch that says "this emblem represents
 *   N8 Guide":
 *       - OFF (default) → STATIC N8      → an N8ONE brand placement
 *       - ON            → BREATHING N8   → N8 Guide at rest (7.5s)
 *       - ON + `active` → BRIGHTER BREATHING → N8 Guide generating (5.5s)
 *
 *   The breath lives inside the orb — a warm inner glow sitting on top
 *   of the sun + path region brightens and softens, giving the sense
 *   that light is emerging from within. The outer halo tracks the same
 *   rhythm as a quiet sunrise bed. `prefers-reduced-motion` disables
 *   the keyframes and substitutes a slightly enhanced static glow so
 *   the Guide still reads as distinct from a static N8ONE emblem.
 *
 * The emblem exists in two bitmaps:
 *   /brand/n8-emblem-512.png   — hero / full mode
 *   /brand/n8-emblem-sm.png    — simplified mode
 * `webp` is also shipped for modern browsers.
 */

const SRC_FULL = "/brand/n8-emblem-512.png";
const SRC_SM = "/brand/n8-emblem-sm.png";
const SRC_WEBP = "/brand/n8-emblem.webp";

export default function N8Emblem({
  mode = "full",
  size = 96,
  breathing = false,
  active = false,
  ariaLabel = "N8ONE",
  className = "",
  testid = "n8-emblem",
}) {
  if (mode === "watermark") {
    return (
      <div
        aria-hidden
        data-testid={`${testid}-watermark`}
        className={`pointer-events-none select-none ${className}`}
        style={{ width: size, height: size, opacity: 0.055 }}
      >
        <picture>
          <source srcSet={SRC_WEBP} type="image/webp" />
          <img
            src={SRC_FULL}
            alt=""
            draggable="false"
            className="block w-full h-full object-contain"
          />
        </picture>
      </div>
    );
  }

  if (mode === "simplified") {
    return (
      <span
        role="img"
        aria-label={ariaLabel}
        data-testid={`${testid}-simplified`}
        className={`inline-block relative align-[-0.14em] select-none ${className}`}
        style={{ width: size, height: size }}
      >
        <img
          src={SRC_SM}
          alt=""
          aria-hidden
          draggable="false"
          className="block w-full h-full object-contain"
          style={{ filter: "drop-shadow(0 1px 2px rgba(140,106,93,0.22))" }}
        />
      </span>
    );
  }

  // full
  const guide = !!breathing;
  const guideActive = !!(breathing && active);
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-testid={testid}
      data-breathing={guide ? "true" : "false"}
      data-active={guideActive ? "true" : "false"}
      className={`relative inline-flex items-center justify-center ${guide ? "n8-emblem-breathing" : ""} ${className}`}
      style={{ width: size, height: size }}
    >
      {/* Ambient warm halo — a soft sunrise bed behind the orb. The
          physical emblem itself NEVER animates anywhere in the app;
          only this halo + the inner "sun/path" glow breathe, and only
          when `breathing` is true (i.e. this emblem represents N8
          Guide). Keyframes + reduced-motion guards live in index.css. */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full n8-emblem-halo"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,205,154,0.55) 0%, rgba(215,176,156,0.28) 42%, rgba(215,176,156,0) 72%)",
          filter: "blur(2px)",
        }}
      />
      <picture>
        <source srcSet={SRC_WEBP} type="image/webp" />
        <img
          src={SRC_FULL}
          alt=""
          aria-hidden
          draggable="false"
          className="relative block w-full h-full object-contain"
          style={{
            filter:
              "drop-shadow(0 10px 36px rgba(184,115,85,0.28)) drop-shadow(0 2px 6px rgba(140,106,93,0.18))",
          }}
        />
      </picture>
      {/* Inner glow positioned over the sun + path region of the
          emblem. The emblem's sun sits in the upper-right quadrant and
          the luminous path curves down through the middle; this
          radial sits over that path. Blend-mode "screen" means we
          brighten warm pixels (the sun / path) without washing the N8
          character. Only renders for the Guide — a static N8ONE emblem
          never carries this layer. */}
      {guide && (
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none n8-emblem-inner-glow"
          style={{
            background:
              "radial-gradient(circle at 62% 42%, rgba(255,218,160,0.75) 0%, rgba(255,190,120,0.4) 22%, rgba(255,170,90,0) 55%)",
            mixBlendMode: "screen",
            borderRadius: "9999px",
          }}
        />
      )}
    </div>
  );
}
